import { RankKit } from "@/components/site/RankKit";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/no-ratio-dscr-loans";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "no ratio DSCR loan",
  "no ratio investor loan",
  "DSCR loan under 1.0",
  "low DSCR loan",
  "negative cash flow rental loan",
  "DSCR loan vacant property",
  "no rent history investment loan",
  "no income no ratio mortgage",
  "DSCR 0.75 loan",
  "second home to rental financing",
  "no ratio DSCR requirements",
  "no ratio DSCR rates",
].join(", ");

const FAQS = [
  {
    q: "What is a No-Ratio DSCR loan?",
    a: "A rental-property loan that skips the debt-service coverage test. Instead of proving rent covers PITIA, you offset the risk with a larger down payment, a stronger credit profile, and more reserves.",
  },
  {
    q: "Who is it for?",
    a: "Investors buying in appreciation markets where rents do not cover payments, properties that are vacant or mid-turnover, short-term rentals with no operating history, and second-home-style assets being converted to rentals.",
  },
  {
    q: "What are the requirements?",
    a: "Generally 680+ FICO, 25–30% down (70–75% maximum LTV), and elevated reserves. Some programs still show a nominal 0.75 floor rather than removing the ratio entirely.",
  },
  {
    q: "How much more does it cost?",
    a: "Expect a rate premium relative to a standard DSCR loan at the same leverage, because the property is not demonstrating coverage. The exact add-on depends on FICO, LTV, and property type.",
  },
  {
    q: "Can I refinance out of it later?",
    a: "Yes. Once the property is leased and the ratio clears 1.00, a rate-and-term refinance into a standard DSCR loan is the common exit.",
  },
  {
    q: "Can I use a No-Ratio DSCR loan for a first purchase in a new market?",
    a: "Yes, it's a common use case when you're buying in an appreciation-focused market where rents don't yet support the payment. The larger down payment and stronger credit profile offset the missing coverage test.",
  },
  {
    q: "Does a No-Ratio loan still require an appraisal with rent estimate?",
    a: "Yes, an appraisal is still ordered and typically includes a rent schedule for file documentation and future refinance planning, even though the ratio itself is not used to qualify the loan.",
  },
  {
    q: "Is a No-Ratio DSCR loan the same as a stated-income loan?",
    a: "No. Stated income references personal income representations, while No-Ratio skips the property coverage test entirely and relies on credit, down payment, and reserves — no personal income figure is used in either case.",
  },
  {
    q: "Can I get a No-Ratio loan on a 2–4 unit property?",
    a: "Yes on most programs, subject to the same reduced leverage and elevated reserve requirements that apply to single-family No-Ratio loans, plus any additional adjustment typical of multi-unit properties.",
  },
  {
    q: "What reserves are required on a No-Ratio DSCR loan?",
    a: "Reserve requirements run higher than a standard DSCR loan given the absence of a qualifying coverage ratio — commonly 6 months or more of PITIA in a verified account, scaled by loan size and leverage.",
  },
  {
    q: "Can a No-Ratio loan be used for a vacant fix-and-hold property?",
    a: "Yes, it's a frequent fit for a property that is vacant, mid-renovation, or between tenants, since there is no lease or stabilized rent yet to test against a coverage ratio.",
  },
  {
    q: "Is a No-Ratio DSCR loan available on a cash-out refinance?",
    a: "Yes on select programs, though cash-out leverage under a No-Ratio structure is generally lower than a No-Ratio purchase, given the added risk of pulling equity from a property that isn't demonstrating rent coverage.",
  },
];

export const Route = createFileRoute("/no-ratio-dscr-loans")({
  head: () =>
    buildHead({
      title: "No-Ratio DSCR Loans — Financing When Rent Doesn't Cover PITIA",
      description:
        "No-Ratio DSCR loans skip the coverage test for vacant, negative-cash-flow, or no-history rentals. 680+ FICO, 70–75% LTV, larger reserves, no tax returns.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "No-Ratio DSCR", url: URL },
      ],
      service: { name: "No-Ratio DSCR Loans" },
      faqs: FAQS,
    }),
  component: NoRatio,
});

function NoRatio() {
  return (
    <SiteShell hideBreadcrumbs productLabel="No-Ratio DSCR">
      <PageHero
        eyebrow="DSCR · No-Ratio"
        title="No-Ratio DSCR loans"
        subtitle="Not every good deal cash-flows on day one. When rent doesn't cover PITIA — a vacant unit, an appreciation-market purchase, or a short-term rental with no operating history — a No-Ratio structure trades the coverage test for more equity."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <RankKit page="no-ratio-dscr-loans" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "No-Ratio DSCR" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Snapshot" },
          { href: "#when", label: "When it fits" },
          { href: "#compare", label: "DSCR vs. No-Ratio" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "A No-Ratio DSCR loan qualifies the property without requiring rent to fully cover PITIA — no DSCR minimum is applied.",
          "The trade-off is leverage and pricing: expect lower maximum LTV (roughly 70–75%) and a higher rate than standard DSCR.",
          "It fits appreciation plays, low-yield metros, vacant or between-tenant properties, and heavy value-add deals.",
          "Credit, reserves, and property quality carry more weight when the ratio is not there to support the file.",
          "All parameters are illustrative program guidelines, not an approval, quote, or commitment to lend.",
        ]}
      />

      <Section id="snapshot">
        <StatRow
          items={[
            { label: "Max LTV", value: "70–75%" },
            { label: "Min FICO", value: "680" },
            { label: "DSCR test", value: "Waived / 0.75 floor" },
            { label: "Reserves", value: "6+ months typical" },
          ]}
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          The trade is straightforward: the lender gives up the coverage test and you give up leverage. Where a standard
          DSCR loan might reach 80% LTV, No-Ratio generally tops out at 70–75% with more months of reserves verified at
          closing.
        </p>
      </Section>

      <div className="bg-muted/40">
        <Section id="when">
          <SectionHeader eyebrow="Fit check" title="When No-Ratio is the right structure" />
          <div className="mt-8">
            <FeatureGrid
              items={[
                { title: "Vacant at closing", body: "No lease and a market rent that lands under PITIA — common in mid-turnover acquisitions." },
                { title: "Appreciation markets", body: "High-price metros where yields are thin but the long-term thesis is equity growth." },
                { title: "New short-term rentals", body: "No T-12 revenue history yet and projections alone won't carry the ratio." },
                { title: "Heavy-payment scenarios", body: "High tax and insurance markets where PITIA outruns achievable rent." },
              ]}
            />
          </div>
        </Section>
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow={"Live pricing · No-ratio"}
        heading={"Current No-Ratio DSCR Rates"}
        paragraphs={[
            "No-ratio structures qualify without a coverage-ratio test, and they are separate programs from standard DSCR with their own eligibility and pricing. The live quotes below are standard DSCR pricing at a qualifying ratio, shown so you can see the structure of a real quote — they are not no-ratio pricing and a no-ratio scenario may price differently.",
            "Because a no-ratio file cannot be priced from the public scenario inputs alone, the accurate way to see your number is a scenario submission where credit, leverage and reserves are known. We do not publish an estimated no-ratio rate, because we would be inventing it.",
        ]}
        faqs={[
            { q: "How much more does a no-ratio DSCR loan cost?", a: "The premium varies by lender, leverage and credit tier, so we do not publish a fixed spread. A loan officer can return the actual difference for your file in writing." },
            { q: "Why can't the rate center price a no-ratio scenario?", a: "The public rate center prices scenarios that carry a qualifying coverage ratio. No-ratio programs are quoted per file against reserves, credit and property profile." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "Price a qualifying DSCR scenario" },
            { to: "/dscr-calculator", label: "Check your ratio with the DSCR calculator" },
            { to: "/dscr-loan-requirements", label: "See DSCR credit and reserve requirements" },
        ]}
      />

      <Section id="compare">
        <SectionHeader eyebrow="Side by side" title="Standard DSCR vs. No-Ratio" />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Factor</th>
                <th className="px-4 py-3">Standard DSCR</th>
                <th className="px-4 py-3">No-Ratio DSCR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["Coverage test", "1.00+ for full leverage", "Waived or 0.75 nominal floor"],
                ["Max LTV", "Up to 80%", "70–75%"],
                ["Min FICO", "660", "680"],
                ["Reserves", "1–6 months PITIA", "6+ months typical"],
                ["Relative pricing", "Baseline", "Premium"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                  <td className="px-4 py-3 text-foreground">{row[1]}</td>
                  <td className="px-4 py-3 text-brand">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <ProsCons
        title="No-Ratio: benefits and trade-offs"
        pros={[
          "Deals that fail the ratio test can still close",
          "No lease or rent history required",
          "Still no tax returns, W-2s, or DTI calculation",
          "Clean refinance path into standard DSCR once leased",
        ]}
        cons={[
          "Lower maximum leverage — more cash at closing",
          "Rate premium over a standard DSCR loan",
          "Higher credit floor and heavier reserve requirement",
          "Negative carry has to be funded from outside the property",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Deal doesn't cash-flow yet?"
        body="Send the numbers anyway. We'll show you the No-Ratio structure alongside the standard DSCR option so you can compare cash to close."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <RankKit page="no-ratio-dscr-loans" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="No-Ratio DSCR questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Business-purpose loans only. Guidelines shown are illustrative and vary by lender, property, and state. Not a
          quote, offer, or commitment to lend. Subject to underwriting and approval.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="no-ratio-dscr" />

      <CTABand
        title="Get a No-Ratio scenario side by side"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
